// src/pages/ReservationsPage.js
import React, { memo, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Box, Container, Typography, CircularProgress } from "@mui/material";
import axios from "axios";
import { dedupeRequest } from "../utils/requestDedupe";
import useCustomToast from "../hooks/useCustomToast";
import Reservations from "../components/Reservations";
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';
import useHotelList from "../hooks/useHotelList";

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

function decryptData(ciphertext) {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

// Use memo to prevent unnecessary re-renders
const ReservationsPage = memo(() => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [waitingForHotelSelection, setWaitingForHotelSelection] = useState(true);
  const { errorToast } = useCustomToast();
  const { hotels } = useHotelList();
  const hotelIdRef = useRef(localStorage.getItem("selectedHotelId") || "");

  // Track hotel and host IDs for effect dependencies
  const [hotelId, setHotelId] = useState(() => localStorage.getItem("selectedHotelId") || "");
  const [hostId, setHostId] = useState(() => localStorage.getItem("selectedHostId") || "");

  // Wait for hotel selection to be properly set
  useEffect(() => {
    const checkHotelSelection = () => {
      const currentHotelId = localStorage.getItem("selectedHotelId");
      const currentHostId = localStorage.getItem("selectedHostId");
      
      // If we have both hotel and host IDs, we're ready
      if (currentHotelId && currentHostId) {
        setHotelId(currentHotelId);
        setHostId(currentHostId);
        setWaitingForHotelSelection(false);
        return true;
      }
      
      // If we have hotels but no selection, try to set the first one
      if (hotels && hotels.length > 0 && !currentHotelId) {
        const firstHotel = hotels[0];
        localStorage.setItem("selectedHotelId", firstHotel.hotel_id);
        localStorage.setItem("selectedHotelName", firstHotel.hotel_name);
        
        // Also set host_id if available
        if (firstHotel.host_id) {
          localStorage.setItem("selectedHostId", firstHotel.host_id);
          setHotelId(firstHotel.hotel_id);
          setHostId(firstHotel.host_id);
          setWaitingForHotelSelection(false);
          return true;
        } else if (currentHostId) {
          // We have host_id but no hotel_id, set the hotel
          setHotelId(firstHotel.hotel_id);
          setHostId(currentHostId);
          setWaitingForHotelSelection(false);
          return true;
        }
      }
      
      return false;
    };

    // Check immediately
    if (checkHotelSelection()) {
      return;
    }

    // If not ready, wait a bit and check again
    const timer = setTimeout(() => {
      if (checkHotelSelection()) {
        return;
      }
      
      // If still not ready after 2 seconds, show error
      setWaitingForHotelSelection(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [hotels]);

  useEffect(() => {
    let isMounted = true;
    
    // Don't fetch if we're still waiting for hotel selection
    if (waitingForHotelSelection) {
      return;
    }

    const fetchBookings = async () => {
      try {
        setLoading(true);
        const token = getDecryptedToken();
        const currentHotelId = localStorage.getItem("selectedHotelId");
        const currentHostId = localStorage.getItem("selectedHostId");
        setHotelId(currentHotelId || "");
        setHostId(currentHostId || "");

        if (!token || !currentHotelId || !currentHostId) {
          if (!token) {
            errorToast("Please log in to view reservations");
          } else if (!currentHotelId || !currentHostId) {
            if (hotels && hotels.length === 0) {
              errorToast("No properties available. Please contact support.");
            } else {
              errorToast("Please select a property first");
            }
          }
          setBookings([]);
          setLoading(false);
          return;
        }

        const response = await dedupeRequest(
          `getHostAllBookings:${currentHostId}:${currentHotelId}`,
          () => {
            const formData = new FormData();
            formData.append("host_id", currentHostId);
            formData.append("hotel_id", currentHotelId);
            formData.append("token", token);
            return axios.post(
              "https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHostAllBookings",
              formData
            );
          },
          4000
        );

        if (isMounted) {
          if (response.data.status) {
            setBookings(response.data.data || []);
          } else {
            errorToast(response.data.message || "Failed to fetch bookings");
            setBookings([]);
          }
        }
      } catch (error) {
        if (isMounted) {
          console.error("Error fetching bookings:", error);
          errorToast("An error occurred while fetching bookings");
          setBookings([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchBookings();

    // Listen for hotel change via localStorage and reload the page if changed from another tab
    const handleStorageChange = (e) => {
      if (e.key === "selectedHotelId") {
        const newHotelId = localStorage.getItem("selectedHotelId") || '';
        if (newHotelId !== hotelIdRef.current) {
          hotelIdRef.current = newHotelId;
          window.location.reload();
        }
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      isMounted = false;
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [errorToast, hotelId, hostId, waitingForHotelSelection]);

  // Keep ref in sync with hotelId (no reload — fetch effect already runs when hotelId/hostId change)
  useEffect(() => {
    hotelIdRef.current = hotelId;
  }, [hotelId]);

  if (waitingForHotelSelection) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: "#FF7700" }} />
        <Typography variant="body2" color="text.secondary">
          Loading property information...
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: "#FF7700" }} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 50 }}
      transition={{ duration: 0.5 }}
      key="reservations-page"
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {bookings.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              backgroundColor: "background.paper",
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No bookings found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              There are no bookings available for this property.
            </Typography>
          </Box>
        ) : (
          <Reservations initialBookings={bookings} />
        )}
      </Container>
    </motion.div>
  );
});

export default ReservationsPage;